/**
 * <linked-variants>
 *
 * Fetches the products that share the current product's `group:<value>` tag and renders them as
 * thumbnail + color-name swatches. The list is produced by templates/search.swatches.liquid.
 *
 * The element stays hidden until it has 2+ products to show, so a product that is not linked to
 * anything (or is the only one in its group) shows nothing.
 */
if (!customElements.get('linked-variants')) {
  class LinkedVariants extends HTMLElement {
    connectedCallback() {
      this.group = (this.dataset.group || '').trim();
      this.currentHandle = this.dataset.currentHandle || '';
      this.fetchUrl = this.dataset.fetchUrl || '';
      this.container = this.querySelector('[data-swatches]');

      if (!this.group || !this.fetchUrl || !this.container) {
        this.hidden = true;
        return;
      }

      this.load();
    }

    async load() {
      try {
        const response = await fetch(this.fetchUrl);
        if (!response.ok) throw new Error('Request failed: ' + response.status);

        const text = await response.text();
        let items;
        try {
          items = JSON.parse(text);
        } catch (error) {
          console.warn('[linked-variants] Could not parse search response as JSON.', error);
          this.hidden = true;
          return;
        }

        this.render(items);
      } catch (error) {
        console.warn('[linked-variants] Failed to load linked variants.', error);
        this.hidden = true;
      }
    }

    render(items) {
      if (!Array.isArray(items) || items.length < 2) {
        this.hidden = true;
        return;
      }

      items.sort((a, b) => String(a.color || '').localeCompare(String(b.color || '')));

      const fragment = document.createDocumentFragment();

      items.forEach((item) => {
        const isCurrent = item.handle === this.currentHandle;
        const label = item.color || item.title || '';

        const node = document.createElement(isCurrent ? 'div' : 'a');
        node.className = 'linked-variants__item' + (isCurrent ? ' is-selected' : '');
        if (!isCurrent && item.url) node.href = item.url;
        if (item.available === false) node.classList.add('is-unavailable');
        node.setAttribute('aria-label', label);
        if (isCurrent) node.setAttribute('aria-current', 'true');

        const thumb = document.createElement('span');
        thumb.className = 'linked-variants__thumb';
        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = label;
          img.loading = 'lazy';
          img.width = 64;
          img.height = 64;
          thumb.appendChild(img);
        }
        node.appendChild(thumb);

        const caption = document.createElement('span');
        caption.className = 'linked-variants__caption';
        caption.textContent = label;
        node.appendChild(caption);

        fragment.appendChild(node);
      });

      this.container.innerHTML = '';
      this.container.appendChild(fragment);
      this.hidden = false;

      // If the current product had no color: tag, fall back to the color the search view found for it.
      if (!this.dataset.currentColor) {
        const current = items.find((item) => item.handle === this.currentHandle);
        const labelEl = this.querySelector('[data-current-color-label]');
        if (current && current.color && labelEl) labelEl.textContent = current.color;
      }
    }
  }

  customElements.define('linked-variants', LinkedVariants);
}
